import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { getMsStatus, createProjectFolder } from "../../../api/microsoft";
import { getProject, getProjects, updateProject, deleteProject, triggerBackfill, getBackfillStatus, retryXeroSync } from "../../../api/projects";
import { getAccountSettings } from "../../../api/settings";
import { setPendingFolderRequest } from "../../../folderRequest";

export function useProjectDetailController({ projectId, navigate }) {
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msConnected, setMsConnected] = useState(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [pendingDropFiles, setPendingDropFiles] = useState(null);
  const [backfillDismissed, setBackfillDismissed] = useState(false);
  const [maxEmails, setMaxEmails] = useState(20);
  const [retryingXero, setRetryingXero] = useState(false);

  const dragDepth = useRef(0);
  const pollRef = useRef(null);

  const loadProject = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [currentProject, allProjects] = await Promise.all([getProject(projectId), getProjects()]);
      setProject(currentProject);
      setProjects(allProjects);
    } catch {
      setError("Could not load this project. It may have been deleted or you may not have access.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  useEffect(() => {
    getMsStatus().then(({ connected }) => setMsConnected(connected)).catch(() => setMsConnected(false));
    getAccountSettings().then((settings) => setMaxEmails(settings?.max_emails_to_process ?? 20)).catch(() => {});
  }, []);

  const handleCreateFolder = useCallback(async () => {
    if (!project) return;
    setCreatingFolder(true);
    try {
      const folder = await createProjectFolder(project.name);
      const updated = await updateProject(project.id, {
        onedrive_folder_id: folder.id,
        onedrive_folder_url: folder.webUrl,
        onedrive_folder_name: folder.name,
      });
      setProject(updated);
      toast.success("OneDrive folder created and linked.");
    } catch (e) {
      toast.error(`Failed to create folder: ${e.response?.data?.detail || e.message}`);
    } finally {
      setCreatingFolder(false);
    }
  }, [project]);

  const handleBrowseFolder = useCallback(() => {
    if (!project?.onedrive_folder_id) return;
    setPendingFolderRequest(project.onedrive_folder_id, project.onedrive_folder_name || project.name);
    navigate("/files");
  }, [navigate, project]);

  const handleDelete = useCallback(async (deleteOneDrive = false) => {
    if (!project) return;
    try {
      await deleteProject(project.id, deleteOneDrive);
      const message = deleteOneDrive
        ? `"${project.name}" and its OneDrive folder deleted.`
        : `"${project.name}" deleted. OneDrive folder was not modified.`;
      toast.success(message);
      navigate("/");
    } catch {
      toast.error("Failed to delete project.");
    }
  }, [navigate, project]);

  const handleFieldSave = useCallback(async (field, value) => {
    if (!project) return;
    try {
      const updated = await updateProject(project.id, { [field]: value });
      setProject(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save. Please try again.");
    }
  }, [project]);

  const canDrop = Boolean(msConnected && project?.onedrive_folder_id);

  const handlePageDragEnter = useCallback((event) => {
    if (!canDrop || !event.dataTransfer.types.includes("Files")) return;
    dragDepth.current += 1;
    if (dragDepth.current === 1) setIsDraggingOver(true);
  }, [canDrop]);

  const handlePageDragLeave = useCallback(() => {
    dragDepth.current -= 1;
    if (dragDepth.current === 0) setIsDraggingOver(false);
  }, []);

  const handlePageDragOver = useCallback((event) => {
    if (!canDrop) return;
    if (event.dataTransfer.types.includes("Files")) event.preventDefault();
  }, [canDrop]);

  const handlePageDrop = useCallback((event) => {
    event.preventDefault();
    dragDepth.current = 0;
    setIsDraggingOver(false);
    if (!canDrop) return;
    const dropped = Array.from(event.dataTransfer.files || []);
    if (dropped.length > 0) setPendingDropFiles(dropped);
  }, [canDrop]);

  const handleTriggerBackfill = useCallback(async () => {
    if (!project) return;
    try {
      await triggerBackfill(project.id);
      setBackfillDismissed(false);
      setProject((current) => current ? {
        ...current,
        backfill_status: "running",
        backfill_progress: {
          stage: "searching_inbox",
          total: 0,
          matched: 0,
          processed: 0,
          current_subject: "",
        },
      } : current);
      toast.success("Inbox scan started — AI is searching for matching emails.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start inbox scan.");
    }
  }, [project]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (!project?.id || project.backfill_status !== "running") return undefined;

    pollRef.current = setInterval(async () => {
      try {
        const status = await getBackfillStatus(project.id);
        setProject((current) => current ? { ...current, ...status } : current);
        if (status.backfill_status !== "running") clearInterval(pollRef.current);
      } catch (_) {}
    }, 5000);

    return () => clearInterval(pollRef.current);
  }, [project?.backfill_status, project?.id]);

  // Poll while the automated Xero invoice sync is in-flight (fire-and-forget
  // background task on the backend) so the banner resolves without a refresh.
  const xeroPollRef = useRef(null);
  useEffect(() => {
    clearInterval(xeroPollRef.current);
    if (!project?.id || project.xero_sync_status !== "pending") return undefined;

    xeroPollRef.current = setInterval(async () => {
      try {
        const fresh = await getProject(project.id);
        setProject(fresh);
        if (fresh.xero_sync_status !== "pending") clearInterval(xeroPollRef.current);
      } catch (_) {}
    }, 4000);

    return () => clearInterval(xeroPollRef.current);
  }, [project?.xero_sync_status, project?.id]);

  const handleRetryXeroSync = useCallback(async () => {
    if (!project) return;
    setRetryingXero(true);
    try {
      const updated = await retryXeroSync(project.id);
      setProject(updated);
      toast.success("Retrying Xero sync…");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry Xero sync.");
    } finally {
      setRetryingXero(false);
    }
  }, [project]);

  return {
    project,
    setProject,
    projects,
    loading,
    error,
    msConnected,
    creatingFolder,
    isDraggingOver,
    pendingDropFiles,
    backfillDismissed,
    setBackfillDismissed,
    maxEmails,
    retryingXero,
    handleCreateFolder,
    handleBrowseFolder,
    handleDelete,
    handleFieldSave,
    handlePageDragEnter,
    handlePageDragLeave,
    handlePageDragOver,
    handlePageDrop,
    handleTriggerBackfill,
    handleRetryXeroSync,
  };
}
